export const featuredProjects = [
    {
    id: 1,
    name: "Midnight Library 🌙",
    meta: ["⟡ Multiplayer", "⟡ Real-time", "⟡ Study app"],
    description:
      "A low-stimulation, cozy library where you can study alongside people who love to stay up past midnight. Time your sessions with the proven Pomodoro method and study next to your avatar and friends for a focused, productive session. Lots of features coming soon.",
    content: (
      <>
        <ul className="list-disc list-inside pl-4">
          <li>
            Built with <strong>Socket.io</strong> for real-time multiplayer study sessions
          </li>
          <li>
            Custom <strong>pixel art avatars</strong> and environments designed in Aseprite
          </li>
          <li>
            Integrated <strong>Pomodoro timer</strong> to keep your sessions focused and productive
          </li>
          <li>
            Maps and room layouts built using <strong>Mappermate</strong>
          </li>
          <li>
            Deployed on <strong>Railway</strong> with a Node.js backend
          </li>
        </ul>
      </>
    ),
    tags: ["JavaScript", "Socket.io", "Node.js", "Canvas", "Aseprite", "Mappermate", "HTML", "CSS", "Github", "Railway"],
    link: "https://midnightlibrary.up.railway.app/",
    linkLabel: "Visit library",
    githubLink: "",
    imageSrc: "images/midnightLibrary.png",
    imageAlt: "Midnight Library screenshot",
    imageHeight: "300px",
    imageFit: "cover",
  },
  {
    id: 2,
    name: "DepReport",
    meta: ["⟡ AI-powered", "⟡ Full-stack", "⟡ Secure by design"],
    description:
      "An internal AI-powered platform that generates professional depreciation reports for BC strata corporations, replacing a manual Excel process. Engineers enter building details and the app pre-loads standard components, uses Claude to draft condition assessments, runs 25-year capital expenditure projections across three funding scenarios, and exports a fully formatted, client-ready PDF. Because it handles client financial data, it's secured with JWT authentication, Row Level Security, and input validation. I built it using AI-assisted development and reviewed and tested the generated code myself.",
    content: (
      <>
        <ul className="list-disc list-inside pl-4">
          <li>
            Pre-loads <strong>76 standard building components</strong> across seven categories on every new project
          </li>
          <li>
            Uses the <strong>Claude API</strong> to generate condition assessments and recommended-work write-ups per component
          </li>
          <li>
            Calculates <strong>25-year capital expenditure projections</strong> with inflation across three funding scenarios
          </li>
          <li>
            Generates an <strong>AI-written executive summary</strong> from the financial data
          </li>
          <li>
            Exports a fully formatted <strong>PDF</strong> (cover page, expenditure tables, component assessments with photos, funding scenarios) via headless Chrome with <strong>Puppeteer</strong>
          </li>
          <li>
            Full-stack: <strong>React + TypeScript</strong> frontend, <strong>Node/Express</strong> backend, <strong>Supabase</strong> (Postgres + storage + auth)
          </li>
          <li>
            Secured with <strong>Supabase JWT authentication</strong>, database-level <strong>Row Level Security</strong>, and <strong>Zod</strong> input validation
          </li>
          <li>
            Built with <strong>Claude as an AI pair programmer</strong> to speed up development; I reviewed and tested all generated code myself, especially the authentication and access-control logic
          </li>
          <li>
            Presented to <strong>company leadership</strong> as an internal product showpiece
          </li>
        </ul>
      </>
    ),
    tags: ["React", "TypeScript", "Vite", "Node.js", "Express", "Claude API", "Supabase", "PostgreSQL", "Row Level Security", "Zod", "Puppeteer", "Tailwind CSS", "AI-assisted development"],
    link: "",
    githubLink: "",
    noLinkLabel: "",
    imageSrc: "images/depReport.png",
    imageAlt: "DepReport",
    imageHeight: "525px",
    imageFit: "cover",

  },
  {
    id: 3,
    name: "Riff",
    meta: ["⟡ Mobile", "⟡ iOS + Android", "⟡ In progress"],
    description:
      "A social app that turns public-speaking practice into a daily habit. Each day you get two random words, record a 60-second video riffing on them, and post to a feed where others react and leave opt-in advice. Built with Expo and TypeScript to ship to both iOS and Android from one codebase.",
    content: (
      <>
        <ul className="list-disc list-inside pl-4">
          <li>
            Daily <strong>two-word prompt</strong> from a deterministic, tested prompt engine
          </li>
          <li>
            Record and share <strong>60-second videos</strong> to a social feed with reactions and opt-in comments
          </li>
          <li>
            Cross-platform <strong>iOS + Android</strong> from a single <strong>Expo (React Native)</strong> codebase, iOS-first
          </li>
          <li>
            <strong>Supabase</strong> for auth, Postgres, and storage — sitting behind a swappable service layer, so the backend can change without touching the UI
          </li>
          <li>
            Single-source-of-truth <strong>theme system</strong>: restyle the whole app from one file
          </li>
          <li>
            Access enforced by <strong>Row Level Security</strong>, with database-level opt-in controls for comments; unit + component tests with <strong>Jest</strong> and React Native Testing Library
          </li>
          <li>
            Status: <strong>working foundation</strong> — architecture, theme, prompt engine, and tests are real and runnable
          </li>
        </ul>
      </>
    ),
    tags: ["React Native", "Expo", "TypeScript", "Supabase", "PostgreSQL", "React Navigation", "Jest"],
    link: "",
    githubLink: "",
    noLinkLabel: "",
    imageSrc: "",
    imageAlt: "Riff",
    imageHeight: "340px",
    imageFit: "contain",
  },
]

export const allProjects = [
  {
    id: 1,
    name: "NBW Dashboard",
    badge: "",
    spotlight: false,
    desc: (
      <p>
        An internal dashboard for Entuitive's Management Team to govern non-billable work — portfolio status by tier, people load and capacity tracking, an impact-effort quadrant built from MT scoring data, a 5-step intake wizard, and a decision log. I rebuilt it from an AI-generated prototype into a full production application over a single weekend using AI-assisted development.
      </p>),
    content: (
      <>
        <ul className="list-disc list-inside pl-4">
          <li>
            Portfolio view of non-billable initiatives by <strong>tier and status</strong>
          </li>
          <li>
            <strong>People load and capacity</strong> tracking with a trade-off analyser
          </li>
          <li>
            <strong>Impact-effort quadrant</strong> analysis built from Management Team scoring data
          </li>
          <li>
            5-step <strong>intake wizard</strong> for proposing new initiatives, with generated email drafts
          </li>
          <li>
            <strong>React + TypeScript + Vite + Tailwind</strong> frontend, <strong>Node/Express</strong> API, <strong>Supabase</strong> (Postgres), charts with <strong>Recharts</strong>
          </li>
          <li>
            Rebuilt an <strong>AI-generated prototype</strong> into a production-ready app in a weekend, using <strong>AI-assisted development</strong> to move quickly
          </li>
          <li>
            Scoring computed from <strong>600+ management scores</strong> across 45 initiatives and a 64-person roster
          </li>
        </ul>
      </>
    ),
    imageSrc: "images/nbwDashboard.png",
    imageAlt: "NBW Dashboard",
    tags: ["React", "TypeScript", "Vite", "Node.js", "Express", "Supabase", "PostgreSQL", "Tailwind CSS", "Recharts", "AI-assisted development"],
    link: "",
    githubLink: "",
    colour: "bg-[#E8DFF5]",
  },
  {
    id: 2,
    name: "Portfolio",
    desc: (
      <p>
        A showcase of my work as a Full Stack Developer and UI/UX Designer. Built with React and Tailwind CSS, this portfolio is fully responsive and designed to highlight my projects in a clean, modern layout.
      </p>),
    content: (
      <>
        <ul className="list-disc list-inside pl-4">
          <li>
            Built with <strong>React</strong> for a dynamic and interactive user experience
          </li>
          <li>
            Styled using <strong>Tailwind CSS</strong>
          </li>
          <li>
            Features a fully <strong>responsive layout</strong> accessible on all devices
          </li>
        </ul>
      </>
    ),
    imageSrc: "images/portfolio.png",
    imageAlt: "Portfolio",
    tags: ["React", "Tailwind CSS", "JavaScript", "UI/UX Design"],
    colour: "bg-[#D9F0FF]",
  },
  {
    id: 3,
    name: "CatGPT",
    desc: (
      <p>
        A playful twist on AI chatbots. CatGpt is a GPT-4o powered assistant that responds entirely like a cat. Built with React and Express, complete with a custom animated cat I designed myself. 
      </p>),
    content: (
      <>
        <ul className="list-disc list-inside pl-4">
          <li>
            Powered by <strong>GPT-4o</strong> via the OpenAI API for intelligent, in-character responses
          </li>
          <li>
            Interactive chat interface built with <strong>React</strong> and styled with <strong>Tailwind CSS</strong>
          </li>
          <li>
            Features a custom <strong>animated cat</strong> designed by me in Procreate
          </li>
          <li>
            Backend built with <strong>Node.js</strong> and <strong>Express</strong>
          </li>
        </ul>
      </>
    ),
    imageSrc: "images/catGPT.png",
    imageAlt: "CatGPT",
    tags: [
      "OpenAI API",
      "Node.js",
      "Express",
      "React",
      "Tailwind CSS",
      "Procreate",
    ],
    link: "https://cat-gpt-delta.vercel.app/",
    githubLink: "https://github.com/sarawasim/catGPT",
    colour: "bg-[#FFF4B3]",
    video: "images/catGpt.mov",
  },
  {
    id: 4,
    name: "SQL Generator",
    desc: (
      <p>
        A tool that turns plain English into SQL queries instantly. Powered by GPT-4o via the OpenAI API, it lets anyone generate complex queries without needing to know the syntax — just describe what you want and copy the result straight to your clipboard.
      </p>),
    content: (
      <>
        <ul className="list-disc list-inside pl-4">
          <li>
            Uses <strong>GPT-4o</strong> to convert plain English descriptions into accurate SQL queries
          </li>
          <li>
            One-click <strong>clipboard functionality</strong> to copy queries instantly
          </li>
          <li>
            Clean, responsive interface built with <strong>React</strong> and <strong>Tailwind CSS</strong>
          </li>
          <li>
            Backend powered by <strong>Node.js</strong> and <strong>Express</strong>, written in <strong>TypeScript</strong>
          </li>
        </ul>
      </>
    ),
    imageSrc: "images/sqlQueryGenerator.png",
    imageAlt: "SQL Query Generator",
    tags: [
      "TypeScript",
      "OpenAI API",
      "Node.js",
      "Express",
      "React",
      "Tailwind CSS",
    ],
    link: "https://sql-query-generator-eight.vercel.app/",
    githubLink: "https://github.com/sarawasim/sqlQueryGenerator",
    colour: "bg-[#D9FFFC]",
    video: "images/sqlQueryGenerator.mov",
  },
  {
    id: 5,
    name: "JavaScript Nightmares",
    desc: (
      <p>
        My very first game. The main character is a programmer-in-training jumping over JavaScript blocks one skill point at a time. Built with vanilla JS and HTML5 Canvas, with custom animations I made in Procreate.
      </p>),
    content: (
      <>
        <ul className="list-disc list-inside pl-4">
          <li>
            Built with <strong>vanilla JavaScript</strong> and the <strong>HTML5 Canvas</strong> API
          </li>
          <li>
            Custom <strong>animations and sprites</strong> designed by me in Procreate
          </li>
          <li>
            Includes a <strong>score counter</strong>, space-to-start, and space-to-restart on death
          </li>
          <li>
            Deployed on <strong>Vercel</strong>
          </li>
        </ul>
      </>
    ),
    imageSrc: "images/bobJumps.png",
    imageAlt: "Bob Jumps",
    tags: ["JavaScript", "HTML", "CSS", "Procreate", "Vercel"],
    link: "https://bob-jumps.vercel.app/",
    githubLink: "https://github.com/sarawasim/bobJumps",
    colour: "bg-[#FFCAE3]",
    video: "images/bobJumps.mov",
  },
  {
    id: 6,
    name: "Collection Tracker",
    desc: (
      <p>
        A waste tracking and supply chain analytics platform built collaboratively with a team of six BCIT students. Businesses use it to log generated waste, coordinate with collectors and processors, and visualize how much material is being diverted from landfills.
      </p>),
    content: (
      <>
        <ul className="list-disc list-inside pl-4">
          <li>
            Developed collaboratively by a team of six <strong>BCIT</strong> Full Stack and Digital Design students
          </li>
          <li>
            Full-stack app built with <strong>React</strong>, <strong>Node.js</strong>, <strong>Express</strong>, and <strong>PostgreSQL</strong>
          </li>
          <li>
            Enables businesses to <strong>track waste</strong> and communicate data to collectors and processors
          </li>
          <li>
            Features <strong>analytics dashboards</strong> showing how much material is diverted from landfills
          </li>
          <li>
            Project managed using <strong>Jira</strong> and deployed on <strong>Heroku</strong>
          </li>
        </ul>
      </>
    ),
    imageSrc: "images/collectionTrackerApp.png",
    imageAlt: "Collection Tracker App",
    tags: ["Postgres", "Express.js", "ReactJS", "Node.js", "Heroku", "Jira"],
    link: "",
    githubLink: "https://github.com/jojo-lam/collectiontrackerapp",
    colour: "bg-[#B5D0FF]",
  },
  {
    id: 7,
    name: "Dynamic Canvas",
    desc: (
      <p>
        Built using the HTML5 Canvas API, this project explores the creative possibilities of generative animation.
      </p>),
    content: (
      <>
        <ul className="list-disc list-inside pl-4">
          <li>
            Built with the <strong>HTML5 Canvas</strong> API using vanilla JavaScript
          </li>
          <li>
            Explores <strong>generative animation</strong> and particle effects in the browser
          </li>
          <li>
            A creative experiment combining my love for <strong>design and code</strong>
          </li>
          <li>
            Deployed on <strong>Vercel</strong>
          </li>
        </ul>
      </>
    ),
    imageSrc: "images/canvas.png",
    imageAlt: "Dynamic Canvas Animation",
    tags: ["JavaScript", "HTML5", "CSS", "Canvas", "Vercel"],
    link: "https://canvas-two-theta.vercel.app/",
    githubLink: "https://github.com/sarawasim/canvas",
    colour: "bg-[#D9FFFC]",
    video: "images/canvas.mov",
  },
]